import { Check } from 'lucide-react';
import { themes } from '@/lib/themes.js';
import { useThemeStore } from '@/stores/theme.js';

/** Unique ordered list of groups, preserving first-appearance order. */
const GROUPS = [...new Map(themes.map((t) => [t.group, t])).values()].map((t) => ({
  group: t.group,
  label: t.name,
}));

/** All themes bucketed by group. */
const BY_GROUP = themes.reduce((acc, t) => {
  (acc[t.group] ??= []).push(t);
  return acc;
}, {});

/** Whether a group has designer-preset themes (no paired counterpart). */
const DESIGNER_GROUPS = new Set(
  GROUPS.filter(({ group }) => (BY_GROUP[group] ?? []).length === 1).map((g) => g.group),
);

export default function ThemePage() {
  const { themeId, setTheme } = useThemeStore();

  return (
    <div className="space-y-8 pb-2">
      {/* Regular palette families */}
      {GROUPS.filter((g) => !DESIGNER_GROUPS.has(g.group)).map(({ group, label }) => (
        <PaletteFamily
          key={group}
          label={label}
          themeList={BY_GROUP[group]}
          activeId={themeId}
          onSelect={setTheme}
        />
      ))}

      {/* Designer presets */}
      {DESIGNER_GROUPS.size > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">
            Designer Presets
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[...DESIGNER_GROUPS].map((group) =>
              (BY_GROUP[group] ?? []).map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  active={theme.id === themeId}
                  onSelect={setTheme}
                />
              )),
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PaletteFamily({ label, themeList, activeId, onSelect }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">
        {label}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {themeList.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            active={theme.id === activeId}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function ThemeCard({ theme, active, onSelect }) {
  const { primary, surface, accent } = theme.preview;

  return (
    <button
      type="button"
      onClick={() => onSelect(theme.id)}
      className={[
        'relative flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all',
        'hover:-translate-y-0.5 hover:shadow-md',
        active
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 shadow-sm ring-2 ring-brand-500/20'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-brand-300',
      ].join(' ')}
    >
      {/* Color dots */}
      <span className="flex gap-1 shrink-0">
        <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: primary }} />
        <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: surface }} />
        <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: accent }} />
      </span>

      {/* Label area */}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
          {theme.name}
        </span>
        <span
          className={[
            'inline-block mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            theme.mode === 'dark'
              ? 'bg-slate-700 text-slate-200'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
          ].join(' ')}
        >
          {theme.mode}
        </span>
      </span>

      {/* Active checkmark */}
      {active && (
        <span className="shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}
